import React, { useState, useEffect } from 'react';
import Spinner from '../components/Spinner';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../services/api';
import { useNotification } from '../components/Notification';
import { getCurrencySymbol } from '../utils/currency';
import TagSelector from '../components/TagSelector';
import EquipmentSelector from '../components/EquipmentSelector';
import ColorSelector from '../components/ColorSelector';
import './Cars.css';

function EditCar({ tenantId }) {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { id } = useParams();
  const [car, setCar] = useState(null);
  const [carNotFound, setCarNotFound] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    customerId: '',
    make: '',
    model: '',
    year: '',
    firstRegistration: '',
    licensePlate: '',
    vin: '',
    mileage: 0,
    hsn: '',
    tsn: '',
    typenschein: '',
    engineCode: '',
    fuelType: '',
    transmission: '',
    color: '',
    reminderDate: '',
    reminderNote: ''
  });
  const [tenant, setTenant] = useState(null);
  const [tags, setTags] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedEquipment, setSelectedEquipment] = useState([]);

  useEffect(() => {
    if (tenantId) {
      loadData();
    }
  }, [tenantId, id]);

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadTenant(),
        loadCustomers(),
        loadTags(),
        loadCar()
      ]);
    } finally {
      setLoading(false);
    }
  };

  const loadTenant = async () => {
    try {
      const data = await api.get('/tenants', null);
      const currentTenant = data.find(t => t.id === tenantId) || data[0];
      setTenant(currentTenant);
    } catch (error) {
      console.error('Error loading tenant:', error);
    }
  };

  const currency = getCurrencySymbol(tenant?.taxSystem || 'DE');

  const loadCar = async () => {
    if (!id || id === 'new') return;
    try {
      console.log('Loading car:', { id, tenantId });
      const data = await api.get(`/cars/${id}`, tenantId);
      console.log('Car loaded:', { id, carId: data.id, tenantId });
      setCar(data);
      setCarNotFound(false);
      const reminderDate = data.reminderDate ? (data.reminderDate.includes('T') ? data.reminderDate.split('T')[0] : data.reminderDate) : '';
      setFormData({
        ...data,
        reminderDate: reminderDate,
        reminderNote: data.reminderNote || ''
      });
      setSelectedTags(data.tags || []);
      setSelectedEquipment(data.equipment || []);
    } catch (error) {
      console.error('Error loading car:', error);
      if (error.response?.status === 404) {
        setCarNotFound(true);
      } else {
        showNotification('Fehler beim Laden des Autos', 'error');
        navigate('/cars');
      }
    }
  };

  const loadCustomers = async () => {
    try {
      const data = await api.get('/customers', tenantId);
      setCustomers(data);
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const loadTags = async () => {
    try {
      const data = await api.get('/tags', tenantId);
      setTags(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error loading tags:', error);
      setTags([]);
    }
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        reminderDate: formData.reminderDate ? new Date(formData.reminderDate).toISOString() : null,
        reminderNote: formData.reminderNote || null,
        tags: selectedTags,
        equipment: selectedEquipment
      };
      
      if (id && id !== 'new') {
        console.log('Updating car:', { id, tenantId, carId: car?.id });
        await api.put(`/cars/${id}`, submitData, tenantId);
        showNotification('Auto erfolgreich aktualisiert', 'success');
      } else {
        console.log('Creating new car:', { tenantId });
        await api.post('/cars', submitData, tenantId);
        showNotification('Auto erfolgreich erstellt', 'success');
      }
      navigate('/cars');
    } catch (error) {
      console.error('Error saving car:', error);
      console.error('Error details:', { 
        id, 
        tenantId, 
        carId: car?.id, 
        response: error.response?.data,
        status: error.response?.status,
        statusText: error.response?.statusText
      });
      let errorMessage = 'Fehler beim Speichern des Autos';
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.status === 404) {
        errorMessage = 'Auto nicht gefunden';
      } else if (error.response?.status === 403) {
        errorMessage = 'Zugriff verweigert';
      } else if (error.response?.status === 400) {
        errorMessage = error.response.data?.error || 'Ungültige Daten';
      } else if (error.message) {
        errorMessage = error.message;
      }
      showNotification(errorMessage, 'error');
    }
  };

  if (loading) {
    return <Spinner />;
  }

  if (id && id !== 'new' && !car && !carNotFound) {
    return null;
  }

  return (
    <div className="cars">
      <div className="page-header">
        <h1>{id ? 'Auto bearbeiten' : 'Neues Auto'}</h1>
        <button type="button" onClick={() => navigate('/cars')} className="btn-secondary">
          Zurück
        </button>
      </div>

      <div className="form-container">
        <form onSubmit={handleSubmit}>
          <div className="form-section">
            <h2>Fahrzeugdaten</h2>
            
            <div className="form-group">
              <label>Kunde *</label>
              <select
                value={formData.customerId}
                onChange={(e) => setFormData({ ...formData, customerId: e.target.value })}
                required
              >
                <option value="">Bitte wählen</option>
                {customers.map(customer => (
                  <option key={customer.id} value={customer.id}>
                    {customer.firstName} {customer.lastName}
                  </option>
                ))}
              </select>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label>Marke *</label>
                <input
                  type="text"
                  value={formData.make}
                  onChange={(e) => setFormData({ ...formData, make: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Modell *</label>
                <input
                  type="text"
                  value={formData.model}
                  onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Baujahr</label>
                <input
                  type="number"
                  value={formData.year}
                  onChange={(e) => setFormData({ ...formData, year: e.target.value === '' ? '' : e.target.value })}
                  onBlur={(e) => {
                    const val = e.target.value === '' ? '' : parseInt(e.target.value) || '';
                    setFormData({ ...formData, year: val });
                  }}
                  min="1900"
                  max={new Date().getFullYear() + 1}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Erstzulassung</label>
                <input
                  type="date"
                  value={formData.firstRegistration ? (formData.firstRegistration.includes('T') ? formData.firstRegistration.split('T')[0] : formData.firstRegistration) : ''}
                  onChange={(e) => setFormData({ ...formData, firstRegistration: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Kennzeichen</label>
                <input
                  type="text"
                  value={formData.licensePlate}
                  onChange={(e) => setFormData({ ...formData, licensePlate: e.target.value.toUpperCase() })}
                />
              </div>
              <div className="form-group">
                <label>FIN/VIN</label>
                <input
                  type="text"
                  value={formData.vin}
                  onChange={(e) => setFormData({ ...formData, vin: e.target.value.toUpperCase() })}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Kilometerstand</label>
                <input
                  type="number"
                  value={formData.mileage}
                  onChange={(e) => setFormData({ ...formData, mileage: e.target.value === '' ? '' : e.target.value })}
                  onBlur={(e) => {
                    const val = e.target.value === '' ? 0 : parseInt(e.target.value) || 0;
                    setFormData({ ...formData, mileage: val });
                  }}
                  min="0"
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Kraftstoffart</label>
                <select
                  value={formData.fuelType || ''}
                  onChange={(e) => setFormData({ ...formData, fuelType: e.target.value })}
                >
                  <option value="">Bitte wählen</option>
                  <option value="BENZIN">Benzin</option>
                  <option value="DIESEL">Diesel</option>
                  <option value="ELEKTRO">Elektro</option>
                  <option value="HYBRID">Hybrid</option>
                  <option value="LPG">LPG</option>
                  <option value="CNG">CNG/Erdgas</option>
                </select>
              </div>
              <div className="form-group">
                <label>Getriebeart</label>
                <select
                  value={formData.transmission || ''}
                  onChange={(e) => setFormData({ ...formData, transmission: e.target.value })}
                >
                  <option value="">Bitte wählen</option>
                  <option value="MANUAL">Manuell</option>
                  <option value="AUTOMATIC">Automatik</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <ColorSelector
                value={formData.color || ''}
                onChange={(color) => setFormData({ ...formData, color })}
                label="Farbe"
              />
            </div>

            <div className="form-group">
              <label>Motorkennbuchstabe</label>
              <input
                type="text"
                value={formData.engineCode || ''}
                onChange={(e) => setFormData({ ...formData, engineCode: e.target.value.toUpperCase() })}
                placeholder="z.B. ANA"
                maxLength="4"
              />
            </div>

            {tenant?.taxSystem === 'CH' ? (
              <div className="form-group">
                <label>Typenschein</label>
                <input
                  type="text"
                  value={formData.typenschein || ''}
                  onChange={(e) => setFormData({ ...formData, typenschein: e.target.value })}
                  placeholder="Typenschein-Nummer"
                />
              </div>
            ) : (
              <div className="form-row">
                <div className="form-group">
                  <label>HSN (Herstellerschlüsselnummer)</label>
                  <input
                    type="text"
                    value={formData.hsn || ''}
                    onChange={(e) => setFormData({ ...formData, hsn: e.target.value.toUpperCase() })}
                    placeholder="z.B. 0001"
                    maxLength="4"
                  />
                </div>
                <div className="form-group">
                  <label>TSN (Typschlüsselnummer)</label>
                  <input
                    type="text"
                    value={formData.tsn || ''}
                    onChange={(e) => setFormData({ ...formData, tsn: e.target.value.toUpperCase() })}
                    placeholder="z.B. ABC"
                  />
                </div>
              </div>
            )}

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.accidentVehicle || false}
                  onChange={(e) => setFormData({ ...formData, accidentVehicle: e.target.checked })}
                />
                <span>Unfallfahrzeug</span>
              </label>
            </div>
          </div>

          <div className="form-section">
            <h2>Weitere Informationen</h2>
            
            <div className="form-group">
              <label>Erinnerungstermin (optional)</label>
              <input
                type="date"
                value={formData.reminderDate ? formData.reminderDate.split('T')[0] : ''}
                onChange={(e) => setFormData({ ...formData, reminderDate: e.target.value ? new Date(e.target.value).toISOString() : '' })}
              />
            </div>

            {formData.reminderDate && (
              <div className="form-group">
                <label>Erinnerungstext (optional)</label>
                <textarea
                  value={formData.reminderNote || ''}
                  onChange={(e) => setFormData({ ...formData, reminderNote: e.target.value })}
                  placeholder="Was soll dem Kunden in Erinnerung gerufen werden?"
                  rows={3}
                />
              </div>
            )}

            <div className="form-group">
              <label>Tags</label>
              <TagSelector
                tenantId={tenantId}
                selectedTags={selectedTags}
                onChange={setSelectedTags}
              />
            </div>

            <div className="form-group">
              <EquipmentSelector
                tenantId={tenantId}
                selectedEquipment={selectedEquipment}
                onChange={setSelectedEquipment}
                make={formData.make}
              />
            </div>
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-primary">
              Speichern
            </button>
            <button
              type="button"
              onClick={() => navigate('/cars')}
              className="btn-secondary"
            >
              Abbrechen
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditCar;

